import { Button, ButtonTheme } from 'components/Button';
import { ButtonGroup } from 'components/ButtonGroup';
import { ActivityIndicator } from 'components/BlockingLoader/ActivityIndicator';
import { Grid, GridProps } from 'components/Grid';
import { Icon, ICONS } from 'components/Icon';
import { IconButton } from 'components/IconButton';
import { Menu } from 'components/Menu';
import { Page } from 'components/Page';
import { PageEmpty } from 'components/PageEmpty';
import { PageHeader } from 'components/PageHeader';
import { PageToolbar } from 'components/PageToolbar';
import { Pill } from 'components/Pill';
import { Pressable } from 'components/Pressable';
import { ReferenceToText } from 'components/ReferenceText';
import { Txt } from 'components/Txt';
import { useSelector } from 'hooks/useSelector';
import { formatNumber, pluralise } from 'lib/pluralise';
import { theme } from 'lib/theme';
import * as React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import { TASK_SEARCH_MAX_LENGTH } from '@riskbase/types';
import { ModalScreens } from 'components/Navigation/types';
import { NAVIGATE_MODAL } from 'store/actions';
import {
  doPropertyAnalysisClear,
  doPropertyAnalysisDatasetDecisionUpdate,
  doPropertyAnalysisExport,
  doPropertyAnalysisFilterOptionsLoad,
  doPropertyAnalysisFilterOpenModal,
  doPropertyAnalysisLoad,
} from 'store/propertyAnalysis/actions';
import {
  selectPropertyAnalysisError,
  selectPropertyAnalysisExportError,
  selectPropertyAnalysisExportLoadStatus,
  selectPropertyAnalysisIsLoaded,
  selectPropertyAnalysisIsLoading,
  selectPropertyAnalysisItems,
  selectPropertyAnalysisFilter,
  selectPropertyAnalysisFilterOptionsLoadStatus,
  selectPropertyAnalysisFilterOptions,
  selectPropertyAnalysisDecisionUpdateError,
  selectPropertyAnalysisDecisionUpdateErrorPropertyId,
  selectPropertyAnalysisDecisionUpdatePropertyId,
  selectPropertyAnalysisPagination,
  selectPropertyAnalysisSummary,
} from 'store/propertyAnalysis/selectors';
import type {
  CssDatasetDecision,
  CssPropertyAnalysisFilterOptions,
  CssPropertyAnalysisItem,
} from 'types/PropertyAnalysis';

type DefinedGridProps = GridProps<
  | 'property'
  | 'height'
  | 'wallConstruction'
  | 'wallRisk'
  | 'age'
  | 'density'
  | 'priority'
  | 'review'
  | 'decision',
  string,
  {
    items: Record<string, CssPropertyAnalysisItem>;
    onPropertyPress: (property: CssPropertyAnalysisItem) => void;
    datasetDecisionOptions: CssPropertyAnalysisFilterOptions['datasetDecisions'];
    onDecisionChange: (
      property: CssPropertyAnalysisItem,
      decision: CssDatasetDecision,
    ) => void;
    decisionUpdatePropertyId: string | null;
    decisionUpdateError: string | null;
    decisionUpdateErrorPropertyId: string | null;
  }
>;

type CssPropertyAnalysisGridExtraData = NonNullable<
  DefinedGridProps['extraData']
>;

const arePropertyAnalysisRowExtraDataEqual = (
  previous: CssPropertyAnalysisGridExtraData,
  next: CssPropertyAnalysisGridExtraData,
  id: string,
) => {
  const previousError =
    previous.decisionUpdateErrorPropertyId === id
      ? previous.decisionUpdateError
      : null;
  const nextError =
    next.decisionUpdateErrorPropertyId === id ? next.decisionUpdateError : null;

  return (
    previous.items[id] === next.items[id] &&
    previous.onPropertyPress === next.onPropertyPress &&
    previous.datasetDecisionOptions === next.datasetDecisionOptions &&
    previous.onDecisionChange === next.onDecisionChange &&
    (previous.decisionUpdatePropertyId === id) ===
      (next.decisionUpdatePropertyId === id) &&
    previousError === nextError
  );
};

const DATASET_DECISIONS: CssDatasetDecision[] = [
  'Pending',
  'Include',
  'Exclude',
];

const isDatasetDecision = (value: string): value is CssDatasetDecision =>
  DATASET_DECISIONS.includes(value as CssDatasetDecision);

export const getDatasetDecision = (value: string | null): CssDatasetDecision =>
  value && isDatasetDecision(value) ? value : 'Pending';

const displayValue = (value: string | null): string => value || '-';

const displayAddress = (value: string | null): string => {
  if (!value) {
    return '-';
  }

  // Keep the two parts of a UK postcode together when the address wraps.
  return value.replace(
    /\b([A-Z]{1,2}\d[A-Z\d]?)\s+(\d[A-Z]{2})\b/gi,
    '$1\u00a0$2',
  );
};

const splitValues = (value: string | null) =>
  value
    ?.split(';')
    .map((item) => item.trim())
    .filter(Boolean) ?? [];

const DelimitedValues: React.FC<{
  value: string | null;
  conflict?: boolean;
}> = ({ value, conflict = false }) => {
  const values = splitValues(value);

  if (!values.length) {
    return <Txt>-</Txt>;
  }

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        minWidth: 0,
        maxWidth: '100%',
        width: '100%',
      }}
    >
      <View
        style={{
          flexDirection: 'column',
          gap: 2,
          alignItems: 'flex-start',
          flex: 1,
          minWidth: 0,
          maxWidth: '100%',
        }}
      >
        {values.map((item) => (
          <Pill
            key={item}
            label={item}
            withIcon={false}
            textSize="xsmall"
            style={{ maxWidth: '100%', minHeight: 22, minWidth: 0 }}
            textStyle={{ flexShrink: 1 }}
          />
        ))}
      </View>
      {conflict && values.length > 1 && (
        <Icon
          source={ICONS.CircleExclamationLight}
          color={theme.colors.orange}
          size={16}
          style={{ flexShrink: 0 }}
        />
      )}
    </View>
  );
};

const StatusPill: React.FC<{
  value: string | null;
  type: 'priority' | 'review' | 'decision';
}> = ({ value, type }) => {
  const normalised = value?.trim().toLowerCase() ?? '';

  const colour = (() => {
    if (type === 'review') {
      return normalised === 'yes'
        ? theme.colors.red
        : normalised === 'no'
          ? theme.colors.green
          : theme.colors.orange;
    }

    if (type === 'decision') {
      return normalised === 'exclude'
        ? theme.colors.red
        : normalised === 'include'
          ? theme.colors.green
          : theme.colors.orange;
    }

    if (normalised === 'very high' || normalised === 'high') {
      return theme.colors.red;
    }
    if (normalised === 'medium') {
      return theme.colors.orange;
    }
    if (normalised === 'low' || normalised === 'very low') {
      return theme.colors.green;
    }
    return theme.colors.black30;
  })();

  return (
    <Pill
      label={displayValue(value)}
      withIcon
      icon={ICONS.CircleSolid}
      iconColor={colour}
      textSize="xsmall"
      style={{ maxWidth: '100%', minHeight: 22, minWidth: 0 }}
      textStyle={{ flexShrink: 1 }}
    />
  );
};

export const DatasetDecisionMenu: React.FC<{
  value: CssDatasetDecision;
  options: CssPropertyAnalysisFilterOptions['datasetDecisions'];
  onChange: (decision: CssDatasetDecision) => void;
  disabled?: boolean;
  error?: string | null;
}> = ({ value, options, onChange, disabled = false, error }) => {
  const currentOption = options.find(
    ({ internalValue }) => internalValue === value,
  );
  const menuOptions = options.flatMap((option) => {
    const decision = option.internalValue;
    if (!isDatasetDecision(decision)) {
      return [];
    }

    return [
      {
        key: decision,
        leftIcon: decision === value ? ICONS.CircleDotSolid : ICONS.CircleLight,
        onPress: () => onChange(decision),
        text: option.name,
      },
    ];
  });

  return (
    <View style={{ alignItems: 'flex-start', gap: 2 }}>
      <Menu
        buttonConfig={{
          backgroundColor: theme.colors.black3,
          chipLike: true,
          disabled: disabled || !menuOptions.length,
          iconRight: ICONS.CaretDownSolid,
          iconRightColor: theme.colors.textBlack64,
          iconRightSize: 11,
          text: currentOption?.name || value,
          textSize: 'xsmall',
          textStyle: { color: theme.colors.black },
          textWeight: 'regular',
          buttonTheme: ButtonTheme.themeless,
        }}
        options={menuOptions}
      />
      {!!error && (
        <Txt size="xxsmall" style={{ color: theme.colors.red }}>
          {error}
        </Txt>
      )}
    </View>
  );
};

const SummaryMetric: React.FC<{
  label: string;
  value: number;
  hint: string;
  colour?: string;
  active: boolean;
  onPress: () => void;
}> = ({
  label,
  value,
  hint,
  colour = theme.colors.black80,
  active,
  onPress,
}) => (
  <Pressable
    onPress={onPress}
    accessibilityRole="button"
    accessibilityLabel={label}
    accessibilityHint={active ? 'Remove this filter' : 'Filter by this total'}
    accessibilityState={{ selected: active }}
    hoveredStyle={{ backgroundColor: theme.colors.black3 }}
    pressedStyle={{ opacity: 0.8 }}
    style={{
      flex: 1,
      minWidth: 150,
      paddingHorizontal: theme.padding,
      paddingVertical: theme.paddingHorizontalHalf,
      backgroundColor: active ? theme.colors.blue5 : theme.colors.white,
      borderColor: active ? theme.colors.blue : theme.colors.white,
      borderRadius: theme.cardBorderRadius,
      borderWidth: 1,
      shadowColor: theme.colors.black,
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.06,
      shadowRadius: 3,
      elevation: 1,
    }}
  >
    <Txt
      size="small"
      weight="medium"
      style={{ color: theme.colors.textBlack64 }}
    >
      {label}
    </Txt>
    <Txt size="xlarge" weight="bold" style={{ color: colour }}>
      {formatNumber(value)}
    </Txt>
    <Txt size="xxsmall" style={{ color: theme.colors.textLight }}>
      {hint}
    </Txt>
  </Pressable>
);

const useGetRowData: DefinedGridProps['getRowData'] = ({ id, extraData }) => {
  const {
    items,
    onPropertyPress,
    datasetDecisionOptions,
    onDecisionChange,
    decisionUpdatePropertyId,
    decisionUpdateError,
    decisionUpdateErrorPropertyId,
  } = extraData;
  const data = items[id];

  return React.useMemo(() => {
    if (!data) {
      return null;
    }

    return {
      onPress: () => onPropertyPress(data),
      columns: {
        property: () => (
          <View style={{ flex: 1 }}>
            <Txt numberOfLines={1} weight="bold">
              {data.name}
            </Txt>
            <Txt
              numberOfLines={2}
              size="xxsmall"
              style={{ color: theme.colors.textBlack64 }}
            >
              {displayAddress(data.address)}
            </Txt>
            <ReferenceToText
              reference={data.reference}
              customReference={data.customReference}
            />
          </View>
        ),
        height: () => (
          <View style={{ gap: 2 }}>
            <DelimitedValues value={data.derived.heightCategory} conflict />
            <DelimitedValues value={data.derived.heightMetres} conflict />
          </View>
        ),
        wallConstruction: () => (
          <DelimitedValues value={data.derived.externalWallConstruction} />
        ),
        wallRisk: () => (
          <DelimitedValues value={data.derived.externalWallRiskIndicators} />
        ),
        age: () => (
          <DelimitedValues value={data.derived.buildingAgeBand} conflict />
        ),
        density: () => (
          <DelimitedValues
            value={data.derived.populationDensityBand}
            conflict
          />
        ),
        priority: () => (
          <StatusPill value={data.derived.screeningPriority} type="priority" />
        ),
        review: () => (
          <StatusPill value={data.derived.reviewRequired} type="review" />
        ),
        decision: () => (
          <DatasetDecisionMenu
            value={getDatasetDecision(data.derived.datasetDecision)}
            options={datasetDecisionOptions}
            onChange={(decision) => onDecisionChange(data, decision)}
            disabled={decisionUpdatePropertyId === data.id}
            error={
              decisionUpdateErrorPropertyId === data.id
                ? decisionUpdateError
                : null
            }
          />
        ),
      },
    };
  }, [
    data,
    datasetDecisionOptions,
    decisionUpdateError,
    decisionUpdateErrorPropertyId,
    decisionUpdatePropertyId,
    onDecisionChange,
    onPropertyPress,
  ]);
};

export const CssPropertyAnalysisList: React.FC = () => {
  const dispatch = useDispatch();
  const items = useSelector(selectPropertyAnalysisItems);
  const summary = useSelector(selectPropertyAnalysisSummary);
  const pagination = useSelector(selectPropertyAnalysisPagination);
  const isLoading = useSelector(selectPropertyAnalysisIsLoading);
  const isLoaded = useSelector(selectPropertyAnalysisIsLoaded);
  const error = useSelector(selectPropertyAnalysisError);
  const exportError = useSelector(selectPropertyAnalysisExportError);
  const exportLoadStatus = useSelector(selectPropertyAnalysisExportLoadStatus);
  const filter = useSelector(selectPropertyAnalysisFilter);
  const filterOptionsLoadStatus = useSelector(
    selectPropertyAnalysisFilterOptionsLoadStatus,
  );
  const filterOptions = useSelector(selectPropertyAnalysisFilterOptions);
  const decisionUpdatePropertyId = useSelector(
    selectPropertyAnalysisDecisionUpdatePropertyId,
  );
  const decisionUpdateError = useSelector(
    selectPropertyAnalysisDecisionUpdateError,
  );
  const decisionUpdateErrorPropertyId = useSelector(
    selectPropertyAnalysisDecisionUpdateErrorPropertyId,
  );

  const itemMap = React.useMemo(
    () => Object.fromEntries(items.map((item) => [item.id, item])),
    [items],
  );

  const loadPage = React.useCallback(
    (page: number) => dispatch(doPropertyAnalysisLoad({ page })),
    [dispatch],
  );

  const handleReloadPress = React.useCallback(() => loadPage(1), [loadPage]);

  const handleExportPress = React.useCallback(() => {
    dispatch(doPropertyAnalysisExport());
  }, [dispatch]);

  const handleSearchChange = React.useCallback(
    (value: string) => {
      dispatch(
        doPropertyAnalysisLoad({
          filter: {
            search: value.trim().slice(0, TASK_SEARCH_MAX_LENGTH),
          },
          page: 1,
        }),
      );
    },
    [dispatch],
  );

  const handleOpenFilters = React.useCallback(() => {
    dispatch(doPropertyAnalysisFilterOpenModal());
  }, [dispatch]);

  const handleSummaryMetricPress = React.useCallback(
    (decision: 'all' | CssDatasetDecision) => {
      dispatch(
        doPropertyAnalysisLoad({
          filter: {
            datasetDecisions:
              decision === 'all'
                ? []
                : filter.datasetDecisions.length === 1 &&
                    filter.datasetDecisions[0] === decision
                  ? []
                  : [decision],
          },
          page: 1,
        }),
      );
    },
    [dispatch, filter.datasetDecisions],
  );

  const summaryMetricIsActive = React.useCallback(
    (decision: 'all' | CssDatasetDecision) =>
      decision === 'all'
        ? filter.datasetDecisions.length === 0
        : filter.datasetDecisions.length === 1 &&
          filter.datasetDecisions[0] === decision,
    [filter.datasetDecisions],
  );

  const handlePropertyPress = React.useCallback(
    (property: CssPropertyAnalysisItem) => {
      dispatch(
        NAVIGATE_MODAL({
          screen: ModalScreens.PropertyAnalysisDetails,
          size: 'large',
          params: { property },
        }),
      );
    },
    [dispatch],
  );

  const handleDecisionChange = React.useCallback(
    (property: CssPropertyAnalysisItem, decision: CssDatasetDecision) => {
      const currentDecision = isDatasetDecision(
        property.derived.datasetDecision || '',
      )
        ? property.derived.datasetDecision
        : 'Pending';

      if (currentDecision === decision) {
        return;
      }

      dispatch(
        doPropertyAnalysisDatasetDecisionUpdate({
          propertyId: property.id,
          decision,
        }),
      );
    },
    [dispatch],
  );

  const filterIsTriggered =
    filter.search.trim().length > 0 ||
    filter.priorities.length > 0 ||
    filter.datasetDecisions.length > 0 ||
    filter.hasConflicts !== undefined ||
    filter.heightCategories.length > 0 ||
    filter.buildingAgeBands.length > 0 ||
    filter.populationDensityBands.length > 0;

  React.useEffect(() => {
    dispatch(doPropertyAnalysisFilterOptionsLoad());
    loadPage(1);
    return () => {
      dispatch(doPropertyAnalysisClear());
    };
  }, [dispatch, loadPage]);

  const [columns] = React.useState<DefinedGridProps['columns']>([
    { name: 'Property', dataIndex: 'property', minWidth: 280, flex: 2 },
    {
      name: 'Height category / height (m)',
      dataIndex: 'height',
      minWidth: 190,
      flex: 1,
    },
    {
      name: 'External wall construction',
      dataIndex: 'wallConstruction',
      minWidth: 180,
      flex: 1,
    },
    {
      name: 'External wall risk indicators',
      dataIndex: 'wallRisk',
      minWidth: 180,
      flex: 1,
    },
    { name: 'Age group', dataIndex: 'age', minWidth: 120, flex: 1 },
    { name: 'Density (units)', dataIndex: 'density', minWidth: 130, flex: 1 },
    { name: 'Decision', dataIndex: 'decision', minWidth: 120, flex: 1 },
    { name: 'Priority', dataIndex: 'priority', minWidth: 110, flex: 1 },
    {
      name: 'Conflicting evidence',
      dataIndex: 'review',
      minWidth: 140,
      flex: 1,
    },
  ]);

  const remainingCount = Math.max(pagination.total - items.length, 0);

  return (
    <Page
      backgroundColor={theme.colors.background}
      header={
        <View>
          <PageHeader
            title="Property Analysis • Cladding Safety Scheme"
            showSidebarButton
          />
          <PageToolbar
            onSearch={handleSearchChange}
            searchValue={filter.search}
            searchOnSubmit
            searchMaxLength={TASK_SEARCH_MAX_LENGTH}
            searchPlaceholder="Search properties, addresses or references..."
          >
            <ButtonGroup>
              <Button
                buttonTheme={ButtonTheme.secondary}
                icon={ICONS.FileLight}
                text={
                  exportLoadStatus === 'loading' ? 'Exporting…' : 'Export CSV'
                }
                onPress={handleExportPress}
                disabled={
                  isLoading || exportLoadStatus === 'loading' || !isLoaded
                }
              />
              <IconButton
                icon={ICONS.RotateRightLight}
                onPress={handleReloadPress}
                disabled={isLoading}
              />
              <IconButton
                icon={ICONS.SlidersUpLight}
                onPress={handleOpenFilters}
                showBadge={filterIsTriggered}
                disabled={isLoading || filterOptionsLoadStatus === 'loading'}
              />
            </ButtonGroup>
          </PageToolbar>
          {!!exportError && (
            <View
              style={{
                paddingHorizontal: theme.padding,
                paddingBottom: theme.paddingHorizontalHalf,
              }}
            >
              <Txt size="xxsmall" style={{ color: theme.colors.red }}>
                {exportError}
              </Txt>
            </View>
          )}
        </View>
      }
    >
      <View style={{ flex: 1 }}>
        <View
          style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: theme.paddingHorizontalHalf,
            paddingHorizontal: theme.padding,
            paddingTop: theme.padding,
            paddingBottom: theme.paddingHorizontalHalf,
          }}
        >
          <SummaryMetric
            label="Properties analysed"
            value={summary.total}
            hint="Portfolio"
            active={summaryMetricIsActive('all')}
            onPress={() => handleSummaryMetricPress('all')}
          />
          <SummaryMetric
            label="Pending"
            value={summary.pending}
            hint="Awaiting decision"
            colour={theme.colors.orange}
            active={summaryMetricIsActive('Pending')}
            onPress={() => handleSummaryMetricPress('Pending')}
          />
          <SummaryMetric
            label="Included"
            value={summary.included}
            hint="In final dataset"
            colour={theme.colors.green}
            active={summaryMetricIsActive('Include')}
            onPress={() => handleSummaryMetricPress('Include')}
          />
          <SummaryMetric
            label="Excluded"
            value={summary.excluded}
            hint="Outside scope"
            active={summaryMetricIsActive('Exclude')}
            onPress={() => handleSummaryMetricPress('Exclude')}
          />
        </View>

        {isLoading && !isLoaded ? (
          <View
            style={{
              flex: 1,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ActivityIndicator size={56} />
          </View>
        ) : error && !isLoaded ? (
          <PageEmpty
            icon={ICONS.RBPlectrumLight}
            text="Unable to Load Property Analysis"
            subtext={error}
            button={{
              text: 'Try again',
              icon: ICONS.RotateRightLight,
              onPress: () => loadPage(1),
            }}
            style={{ backgroundColor: theme.colors.background }}
          />
        ) : !items.length ? (
          <PageEmpty
            icon={ICONS.RBPlectrumLight}
            text="No Property Analysis Results"
            subtext="CSS analysis results will appear here when they are available."
            style={{ backgroundColor: theme.colors.background }}
          />
        ) : (
          <View style={{ flex: 1, position: 'relative' }}>
            {error && isLoaded && (
              <View
                style={{
                  marginHorizontal: theme.padding,
                  marginBottom: theme.padding,
                  padding: theme.padding,
                  borderRadius: theme.cardBorderRadius,
                  backgroundColor: theme.colors.white,
                }}
              >
                <Txt
                  weight="bold"
                  style={{
                    color: theme.colors.red,
                    marginBottom: theme.paddingHorizontalHalf,
                  }}
                >
                  Unable to load more Property Analysis results
                </Txt>
                <Txt style={{ color: theme.colors.fieldValueText }}>
                  {error}
                </Txt>
                <Button
                  text="Try again"
                  icon={ICONS.RotateRightLight}
                  onPress={() => loadPage(pagination.page + 1)}
                  style={{ alignSelf: 'flex-start', marginTop: theme.padding }}
                />
              </View>
            )}
            <Grid
              columns={columns}
              data={items.map((item) => item.id)}
              extraData={{
                items: itemMap,
                onPropertyPress: handlePropertyPress,
                datasetDecisionOptions: filterOptions.datasetDecisions,
                onDecisionChange: handleDecisionChange,
                decisionUpdatePropertyId,
                decisionUpdateError,
                decisionUpdateErrorPropertyId,
              }}
              getRowData={useGetRowData}
              areRowExtraDataEqual={arePropertyAnalysisRowExtraDataEqual}
              keyExtractor={(id) => id}
              ListFooterComponent={
                pagination.page < pagination.totalPages ? (
                  <View
                    style={{
                      padding: theme.padding * 2,
                      paddingTop: theme.padding,
                      alignItems: 'flex-start',
                    }}
                  >
                    <Button
                      text={pluralise(
                        remainingCount,
                        '{x} More Result, Load More',
                        '{x} More Results, Load More',
                      )}
                      onPress={() => loadPage(pagination.page + 1)}
                      disabled={isLoading}
                      icon={ICONS.ArrowDownLight}
                    />
                  </View>
                ) : (
                  <View style={{ height: theme.padding * 2 }} />
                )
              }
            />
            {isLoading && isLoaded && (
              <View
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bottom: 0,
                  left: 0,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: theme.colors.white50,
                  zIndex: 2,
                }}
              >
                <ActivityIndicator size={56} />
              </View>
            )}
          </View>
        )}
      </View>
    </Page>
  );
};
